// Timezone helpers for displaying event times in the sheet's local zone.
//
// Events store time as a "naive" HH:MM string plus a short timezone code
// (`event.timezone`) like "ET" / "PT" / "CET". Sheets carry an IANA name
// (e.g. "America/Argentina/Buenos_Aires") auto-derived from the city.
//
// We convert at render time so the same event reads as 4:00 PM on a BA
// sheet and 3:00 PM ET on a NYC sheet — single source of truth on disk,
// localized to wherever the sheet's "happening". DST-aware via Intl.

// Map short codes used by the SheetBuilder tz picker to IANA names. Add
// here if the picker grows. Falls through to the input string if it
// already looks like an IANA name.
const TZ_SHORT_TO_IANA = {
  ET:   'America/New_York',
  EST:  'America/New_York',
  EDT:  'America/New_York',
  CT:   'America/Chicago',
  CST:  'America/Chicago',
  CDT:  'America/Chicago',
  MT:   'America/Denver',
  MST:  'America/Denver',
  MDT:  'America/Denver',
  PT:   'America/Los_Angeles',
  PST:  'America/Los_Angeles',
  PDT:  'America/Los_Angeles',
  GMT:  'Etc/GMT',
  UTC:  'Etc/UTC',
  CET:  'Europe/Berlin',
  CEST: 'Europe/Berlin',
  JST:  'Asia/Tokyo',
  AEST: 'Australia/Sydney',
  AEDT: 'Australia/Sydney',
};

export function ianaForTz(tz) {
  if (!tz) return null;
  if (TZ_SHORT_TO_IANA[tz]) return TZ_SHORT_TO_IANA[tz];
  // Intl.DateTimeFormat rejects "GMT-5" / "GMT+9" — these are offsets,
  // not zones. Some IANAs (America/Bogota, America/Lima, Buenos_Aires,
  // anywhere without a friendly TLA) come back from the server stamped
  // with this shape because shortCodeForIana fell back to the offset.
  // Returning null lets convertTimeBetweenTz short-circuit cleanly
  // instead of throwing into React's render and white-screening.
  if (/^GMT[+-]\d+/i.test(tz)) return null;
  return tz;
}

// Given an event with a tz code different from the sheet's tz, return
// the time read in the sheet's local zone. If anything is missing or the
// zones resolve to the same thing, returns the input unchanged so the
// caller can blindly substitute it without conditional checks.
export function convertTimeBetweenTz(timeHHMM, fromTz, toTz, dateISO) {
  if (!timeHHMM || !fromTz || !toTz || !dateISO) return timeHHMM;
  const fromIana = ianaForTz(fromTz);
  const toIana   = ianaForTz(toTz);
  if (!fromIana || !toIana || fromIana === toIana) return timeHHMM;

  const [h, m] = timeHHMM.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return timeHHMM;

  // Walk the naive time → real UTC instant → naive in target zone:
  //   1. Pretend the naive time is UTC.
  //   2. Ask Intl what `fromIana` would render at that instant.
  //   3. Shift the instant by (desired - rendered) minutes.
  //   4. Render that corrected instant in `toIana`.
  // Wrapped in try/catch so a malformed tz string can't crash the
  // whole render tree — falls back to the original input.
  try {
    const pretendUtc = new Date(`${dateISO}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00Z`);
    const getHM = (date, tz) => {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit',
      }).formatToParts(date);
      return [
        +parts.find(p => p.type === 'hour').value,
        +parts.find(p => p.type === 'minute').value,
      ];
    };
    const [shownH, shownM] = getHM(pretendUtc, fromIana);
    const diffMin = (h - shownH) * 60 + (m - shownM);
    const corrected = new Date(pretendUtc.getTime() + diffMin * 60 * 1000);
    const [outH, outM] = getHM(corrected, toIana);
    return `${String(outH).padStart(2,'0')}:${String(outM).padStart(2,'0')}`;
  } catch {
    return timeHHMM;
  }
}

// True when the event has a tz that differs from the sheet — caller's
// signal that conversion + the source-tz badge should both render.
export function isOutOfSheetZone(event, sheetTz) {
  if (!event?.timezone || !sheetTz) return false;
  return ianaForTz(event.timezone) !== ianaForTz(sheetTz);
}

// Format a UTC ISO instant in the target IANA zone as HH:MM (24h).
// Mirror of the server's `lib/instant.js#formatInstantInZone` so the
// admin pages can read straight off `event.startInstant` instead of
// re-deriving the wall-clock from the legacy timeStart+timezone pair.
// Returns null on bad input so callers can fall back cleanly.
export function formatInstantInZone(instantISO, tz) {
  if (!instantISO) return null;
  const d = new Date(instantISO);
  if (Number.isNaN(d.getTime())) return null;
  const iana = ianaForTz(tz) || 'UTC';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: iana, hour12: false, hour: '2-digit', minute: '2-digit',
    }).formatToParts(d);
    const h = parts.find(p => p.type === 'hour').value;
    const m = parts.find(p => p.type === 'minute').value;
    return `${h}:${m}`;
  } catch {
    return null;
  }
}

// One-shot helper: returns { localTime, sourceLabel } where localTime
// is the sheet-local HH:MM and sourceLabel is "3:00 PM ET" for the
// indicator badge (or null when no conversion is needed).
//
// Phase 3 of the instants migration: when `event.startInstant` is
// populated (everything written since 2026-05-04), the local time is
// formatted directly off the UTC instant in the sheet's zone — no more
// round-tripping through the naive HH:MM + short-code path. The legacy
// `convertTimeBetweenTz` route is retained as a fallback for events
// the backfill skipped (no time at all) and for any older clients
// passing pre-instant payloads.
export function localizeEventTime(event, sheetTz, fmt12) {
  const t = event?.timeStart;
  const instant = event?.startInstant || null;
  if (!t && !instant) return { localTime: '', sourceLabel: null };

  // Flights anchor to the airport's local tz — your watch at LAS
  // reads source time on landing. Render in source AND show the
  // sheet-local time as the badge so the home-base team has a
  // correlation point ("12:20 PM (3:20 PM ET)").
  if (event?.travelType === 'flight_departure' || event?.travelType === 'flight_arrival') {
    if (event.timezone && sheetTz) {
      const sourceHHMM = instant ? (formatInstantInZone(instant, event.timezone) || t) : t;
      const sheetHHMM = instant
        ? formatInstantInZone(instant, sheetTz)
        : convertTimeBetweenTz(t, event.timezone, sheetTz, event.date || '');
      if (sheetHHMM && sheetHHMM !== sourceHHMM) {
        const sheetShort = shortCodeForIana(sheetTz);
        const label = sheetShort ? ` ${sheetShort}` : '';
        return {
          localTime: sourceHHMM,
          sourceLabel: `${fmt12 ? fmt12(sheetHHMM) : sheetHHMM}${label}`,
        };
      }
      return { localTime: sourceHHMM, sourceLabel: null };
    }
    return { localTime: instant ? (formatInstantInZone(instant, sheetTz) || t) : t, sourceLabel: null };
  }

  if (!isOutOfSheetZone(event, sheetTz)) {
    const local = instant ? (formatInstantInZone(instant, sheetTz) || t) : t;
    return { localTime: local, sourceLabel: null };
  }
  const localHHMM = instant
    ? formatInstantInZone(instant, sheetTz)
    : convertTimeBetweenTz(t, event.timezone, sheetTz, event.date || '');
  const sourceHHMM = instant
    ? (formatInstantInZone(instant, event.timezone) || t)
    : t;
  // Prefer a curated short code; if the source tz is a full IANA
  // (e.g. "America/Bogota"), fall back to the city portion so the
  // badge reads "Bogota" instead of dumping the path.
  const sourceLabel = shortCodeForIana(ianaForTz(event.timezone))
    || (event.timezone.includes('/') ? event.timezone.split('/').pop().replace(/_/g, ' ') : event.timezone);
  return {
    localTime: localHHMM,
    sourceLabel: `${fmt12 ? fmt12(sourceHHMM) : sourceHHMM} ${sourceLabel}`,
  };
}

// Reverse of TZ_SHORT_TO_IANA. We mint a code only when it's curated
// here — otherwise return null and let callers fall back to the IANA
// itself. Asking Intl for a `short` value would alias America/Panama
// to "EST", which TZ_SHORT_TO_IANA then resolves to America/New_York
// (1 hour off during US DST). Better to keep the IANA than misroute.
const IANA_TO_SHORT = {
  'America/New_York':       'ET',
  'America/Detroit':        'ET',
  'America/Toronto':        'ET',
  'America/Chicago':        'CT',
  'America/Denver':         'MT',
  'America/Phoenix':        'MST',
  'America/Los_Angeles':    'PT',
  'America/Anchorage':      'AKT',
  'Pacific/Honolulu':       'HST',
  'America/Argentina/Buenos_Aires': 'ART',
  'America/Sao_Paulo':      'BRT',
  'America/Mexico_City':    'CT',
  'Etc/UTC':                'UTC',
  'Etc/GMT':                'GMT',
  'Europe/London':          'GMT',
  'Europe/Berlin':          'CET',
  'Europe/Paris':           'CET',
  'Europe/Madrid':          'CET',
  'Europe/Rome':            'CET',
  'Asia/Tokyo':             'JST',
  'Asia/Singapore':         'SGT',
  'Asia/Dubai':             'GST',
  'Australia/Sydney':       'AET',
};

export function shortCodeForIana(iana) {
  if (!iana) return null;
  return IANA_TO_SHORT[iana] || null;
}
